import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  useLoginMutation,
  useSendLoginOtpMutation,
  useVerifyLoginOtpMutation,
} from '@/features/auth/api/useAuthMutations';
import { getMobileConfig } from '@/features/auth/utils/mobileNumber';
import {
  emptyLoginOtp,
  isPhoneLikeIdentifier,
  LOGIN_OTP_LENGTH,
  toPayloadIdentifier,
  type LoginFormValues,
  type LoginMethod,
} from '@/features/auth/utils/loginIdentifier';

export function useLoginPage() {
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('otp');
  const [otpSent, setOtpSent] = useState(false);
  const [otpSentTo, setOtpSentTo] = useState('');
  const [resendSeconds, setResendSeconds] = useState(0);
  const [otpDigits, setOtpDigits] = useState(emptyLoginOtp);
  const [otpError, setOtpError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<LoginFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      identifier: '',
      rememberMe: false,
    },
  });

  // Clear any previously persisted login email so the field stays empty.
  useEffect(() => {
    localStorage.removeItem('rememberedEmail');
  }, []);

  const identifier = form.watch('identifier', '');
  const phoneConfig = useMemo(() => getMobileConfig(), []);
  // Only show +91/+971 chrome when the value actually looks like a mobile
  // (valid first digit). Digits like "111…" stay in email mode so
  // addresses such as 111@mailinator.com can be typed.
  const isPhoneInput = isPhoneLikeIdentifier(identifier, phoneConfig);

  const loginMutation = useLoginMutation();
  const sendOtpMutation = useSendLoginOtpMutation();
  const verifyOtpMutation = useVerifyLoginOtpMutation();

  const isLoading =
    loginMutation.isPending ||
    sendOtpMutation.isPending ||
    verifyOtpMutation.isPending;

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const id = window.setInterval(() => {
      setResendSeconds((seconds) => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendSeconds]);

  const resetOtpDigits = () => {
    setOtpDigits(emptyLoginOtp());
    setOtpError('');
  };

  const switchMethod = (method: LoginMethod) => {
    setLoginMethod(method);
    setOtpSent(false);
    resetOtpDigits();
    form.setValue('password', '');
  };

  const handleSendOtp = async () => {
    const valid = await form.trigger('identifier');
    if (!valid) return;

    const payloadIdentifier = toPayloadIdentifier(form.watch('identifier'));

    // A code is still live for this identifier, and the API rejects a resend
    // until the cooldown ends — reuse it instead of asking for a new one.
    if (payloadIdentifier === otpSentTo && resendSeconds > 0) {
      setOtpSent(true);
      return;
    }

    sendOtpMutation.mutate(
      { identifier: payloadIdentifier },
      {
        onSuccess: () => {
          setOtpSentTo(payloadIdentifier);
          setOtpSent(true);
          setResendSeconds(120);
          resetOtpDigits();
        },
      },
    );
  };

  const onSubmit = (formData: LoginFormValues) => {
    const payloadIdentifier = toPayloadIdentifier(formData.identifier);

    if (loginMethod === 'password') {
      loginMutation.mutate({
        identifier: payloadIdentifier,
        password: formData.password || '',
        rememberMe: formData.rememberMe,
      });
      return;
    }

    if (!otpSent) {
      void handleSendOtp();
      return;
    }

    const otpString = otpDigits.join('');
    if (otpString.length !== LOGIN_OTP_LENGTH) {
      setOtpError(`Please enter the ${LOGIN_OTP_LENGTH}-digit OTP`);
      return;
    }

    verifyOtpMutation.mutate({
      identifier: payloadIdentifier,
      otp: otpString,
      rememberMe: formData.rememberMe,
    });
  };

  const submitLabel =
    loginMethod === 'password'
      ? 'Sign in'
      : otpSent
        ? 'Verify & sign in'
        : 'Send OTP';

  return {
    form,
    loginMethod,
    otpSent,
    resendSeconds,
    otpDigits,
    setOtpDigits,
    otpError,
    setOtpError,
    showPassword,
    setShowPassword,
    phoneConfig,
    isPhoneInput,
    isLoading,
    submitLabel,
    switchMethod,
    handleSendOtp,
    resetOtpDigits,
    setOtpSent,
    onSubmit,
  };
}
